import {
    APP_EMITTER_EVENTS,
    AppEmitter,
    Button,
    EditableTable,
    FormLabel,
    Icon,
    InputCell,
    stringId,
} from "shared";
import { ITemplateTableComponent } from "entities/template";
import { AppLang } from "app";
import cn from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { UniqueIdentifier } from "@dnd-kit/core";
import { IValidationFunc } from "../../../../lib/types/validation";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import styles from "./document_form_table.module.scss";

interface ITableRecord extends Record<string, string | UniqueIdentifier> {
    id: UniqueIdentifier;
}

interface IDocumentFormTableProps {
    component: ITemplateTableComponent;
    onChange: (partial: Record<string, any>) => void;
    enLanguageEnabled: boolean;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    externalUpdateRecord: Record<string, any>;
    label?: string;
    tooltip: IComponentTooltipSettings;
    readonly: boolean;
}

export const DocumentFormTable: React.FC<IDocumentFormTableProps> = React.memo(({
                                                                                    component,
                                                                                    label,
                                                                                    readonly,
                                                                                    tooltip,
                                                                                    externalUpdateRecord,
                                                                                    onValidationRegister,
                                                                                    enLanguageEnabled,
                                                                                    onChange,
                                                                                }) => {
    const t = useComponentsTranslations();

    const createEmptyRecord = () => {
        const record: ITableRecord = { id: stringId() };
        if (component.list && component.list.length > 0) {
            for (let i = 0; i < component.list.length; i++) {
                record[i.toString()] = "";
            }
        }
        return record;
    };

    const [data, setData] = useState<ITableRecord[]>([createEmptyRecord()]);
    const [isValid, setIsValid] = useState<boolean>(true);
    const { i18n } = useTranslation();

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        };
    }, []);

    useEffect(() => {
        return () => {
            onValidationRegister(() => {
                return Promise.resolve(true);
            }, component.name);
            onChange({ [component.name]: undefined });
        };
    }, []);

    useEffect(() => {
        if (component.name) {
            const tableValue = externalUpdateRecord[component.name];
            if (tableValue) {
                const tableData: ITableRecord[] = [];
                const parsedData = JSON.parse(tableValue);
                parsedData.forEach((row: string[]) => {
                    const rowData = createEmptyRecord();
                    row.forEach((val, index) => {
                        rowData[index.toString()] = val;
                    });
                    tableData.push(rowData);
                });
                setData(tableData);
            } else {
                setData([createEmptyRecord()]);
            }
        }
    }, [externalUpdateRecord, component.name]);

    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<ITableRecord>();
        const ruList = component.list;
        // @ts-ignore
        const enList = enLanguageEnabled && component.list_en && Array.isArray(component.list_en) && component.list_en.filter(el => !!el).length > 0 ?
            component.list_en : ruList;
        const columnList = i18n.language === AppLang.RU ? ruList : enList;

        if (columnList && columnList.length > 0) {
            return columnList.map((column, index) => {
                return columnHelper.accessor(index.toString(), {
                    id: index.toString(),
                    header: column,
                    cell: (info) => {
                        const { id } = info.row.original;
                        return <InputCell
                            readonly={readonly}
                            onPaste={(pasteValue) => {
                                onCellPaste(id as string, index, pasteValue);
                            }}
                            value={info.getValue() as string | undefined}
                            onChange={(text) => {
                                onCellChange(id as string, index, text);
                            }} className={styles.commentCell}/>;
                    }
                });
            });
        } else {
            return null;
        }
    }, [i18n.language, component, enLanguageEnabled]);

    const required = useMemo(() => {
        return component.required !== false && !!columns && columns.length > 0;
    }, [component]);

    useEffect(() => {
        setIsValid(true);
        if (component.name) {
            onValidationRegister(validateTable, component.name);
            onChange({ [component.name]: JSON.stringify(transformData(data)) });
        }
    }, [component.name, data, required]);

    const handleClearEvent = () => {
        if (!readonly) {
            setData([createEmptyRecord()]);
        }
    };

    const onCellPaste = (rowId: string, colIndex: number, pasteArr: string[][]) => {
        setData((prevState) => {
            const updatedState = [...prevState];
            const rowIndex = updatedState.findIndex((r) => r.id === rowId);

            pasteArr.forEach((rowVal, pasteRowIndex) => {
                const calculatedRowIndex = rowIndex + pasteRowIndex;
                if (!updatedState[calculatedRowIndex]) {
                    updatedState.push(createEmptyRecord());
                }
                const row = updatedState[calculatedRowIndex];
                rowVal.forEach((colVal, pasteColIndex) => {
                    const calculatedColumnIndex = colIndex + pasteColIndex;
                    if (row.hasOwnProperty(calculatedColumnIndex.toString())) {
                        row[calculatedColumnIndex] = colVal;
                    }
                });
            });
            return updatedState;
        });
    };

    const onCellChange = (rowId: string, index: number, text: string) => {
        setData((prevState) => {
            const updatedState = [...prevState];
            const recordIndex = updatedState.findIndex((d) => d.id === rowId);
            const updatedRecord = { ...updatedState[recordIndex] };
            updatedRecord[index] = text;
            updatedState[recordIndex] = updatedRecord;
            return updatedState;
        });
    };

    const clearTable = () => {
        setData([createEmptyRecord()]);
    };

    const transformData = (tData: ITableRecord[]): string[][] => {
        const tableData: string[][] = [];

        tData.forEach((row) => {
            const rowData: string[] = [];
            Object.keys(row).forEach((key) => {
                if (key !== "id") {
                    //@ts-ignore
                    rowData[parseInt(key)] = row[key];
                }
            });
            tableData.push(rowData);
        });

        return tableData;
    };

    const isTableValid = (): boolean => {
        const tData = transformData(data);
        return tData.length > 1 || tData.flat(1).filter(s => !!s).length > 0;
    };

    const validateTable = (): Promise<boolean> => {
        const valid = !required || isTableValid();
        setIsValid(valid);
        return Promise.resolve(valid);
    };

    const onAddTableRow = () => {
        setData((prevState) => {
            return [...prevState, createEmptyRecord()];
        });
    };

    const onDeleteRow = (id: UniqueIdentifier) => {
        setData((prevState) => {
            const updatedState = [...prevState];
            const indexToRemove = updatedState.findIndex((d) => d.id === id);
            updatedState.splice(indexToRemove, 1);
            return updatedState.length > 0 ? updatedState : [createEmptyRecord()];
        });
    };

    return columns && columns.length > 0 ?
        <FormLabel title={label}
                   className={styles.labelContainer}
                   disabled={readonly}
                   required={required}
                   titleClassName={readonly ? styles.labelTitleReadonly : styles.labelTitle}>
            {!readonly && <Button className={styles.deleteButton}
                                  onClick={clearTable}>
                <div className={styles.buttonInnerContainer}>
                    <Icon glyph="trash" className={styles.deleteIcon}/>
                    {t("clearTable")}
                </div>
            </Button>}
            <div className={styles.container}>
                <div className={styles.tableContainer}>
                    <EditableTable<ITableRecord> columns={columns}
                                                 readonly={readonly}
                                                 data={data}
                                                 onChange={setData}
                                                 onDeleteRow={onDeleteRow}/>
                    {!readonly && <Button size="s"
                                          className={styles.addButton}
                                          onClick={onAddTableRow}
                                          view="outline">
                        <div className={styles.plusCircle}>
                            <Icon glyph="plusCircle"/>
                        </div>
                    </Button>}
                </div>

                <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltip}/>
            </div>
            {!isValid && <div className="Textinput_state_error">
                <div className={cn(styles.validationError, "Textinput-Hint")}>
                    {t("requiredFieldError")}
                </div>
            </div>}
        </FormLabel> : <FormLabel title={label}
                                  className={styles.labelContainer}
                                  disabled={readonly}
                                  required={required}>
            <div className={styles.emptyColumnsMessage}>
                {t("addColumnsInTemplate")}
            </div>

        </FormLabel>;
});
